import {
  getIndexPagination,
  getIndexPaginationSearchParams,
  normalizeIndexPagination,
  INDEX_PAGE_SIZES,
} from '@shared/lib/pagination';
import { PageContainer } from '@shared/ui';
import { Flex, Tabs } from '@mantine/core';
import {
  ReviewsFilters,
  ReviewsTable,
  useReviewDetailModal,
  type ReviewsListFilter,
} from '@features/reviews';
import {
  useGetCandidateReviews,
  type CandidateReview,
} from '@entities/candidateReview';
import {
  useGetCompanyReviews,
  type CompanyReview,
} from '@entities/companyReview';
import { PaginateType, type SortQuery } from '@shared/types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

const TAB_COMPANY_REVIEWS = 'company-reviews';
const TAB_CANDIDATE_REVIEWS = 'candidate-reviews';

export const ReviewsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openCandidateReviewDetailModal, openCompanyReviewDetailModal } =
    useReviewDetailModal();

  const tabParam = searchParams.get('tab');
  const activeTab =
    tabParam === TAB_CANDIDATE_REVIEWS || tabParam === TAB_COMPANY_REVIEWS
      ? tabParam
      : searchParams.get('companyId') && !searchParams.get('candidateId')
        ? TAB_CANDIDATE_REVIEWS
        : TAB_COMPANY_REVIEWS;

  const [companyReviewsFilter, setCompanyReviewsFilter] =
    useState<ReviewsListFilter>({});
  const [candidateReviewsFilter, setCandidateReviewsFilter] =
    useState<ReviewsListFilter>({});

  const [companyReviewsSort, setCompanyReviewsSort] = useState<SortQuery>({
    id: 'desc',
  });
  const [candidateReviewsSort, setCandidateReviewsSort] = useState<SortQuery>({
    id: 'desc',
  });

  const { page, pageSize } = getIndexPagination(searchParams);

  useEffect(() => {
    const normalized = normalizeIndexPagination(searchParams);
    if (normalized.toString() !== searchParams.toString()) {
      setSearchParams(normalized, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setPage = (nextPage: number) => {
    setSearchParams(
      getIndexPaginationSearchParams(searchParams, nextPage, pageSize)
    );
  };
  const setPageSize = (size: number) => {
    setSearchParams(getIndexPaginationSearchParams(searchParams, 1, size));
  };

  const companyReviewsQuery = useGetCandidateReviews(
    {
      filter: companyReviewsFilter,
      sort: companyReviewsSort,
      page,
      perPage: pageSize,
      paginate: PaginateType.DEFAULT,
    },
    activeTab === TAB_COMPANY_REVIEWS
  );

  const candidateReviewsQuery = useGetCompanyReviews(
    {
      filter: candidateReviewsFilter,
      sort: candidateReviewsSort,
      page,
      perPage: pageSize,
      paginate: PaginateType.DEFAULT,
    },
    activeTab === TAB_CANDIDATE_REVIEWS
  );

  const handleTabChange = (value: string | null) => {
    if (!value) {
      return;
    }

    const next = new URLSearchParams();
    next.set('tab', value);
    next.set('page', '1');
    next.set('perPage', pageSize.toString());
    setSearchParams(next);
  };

  const handleCompanyReviewsSort = (
    field: string,
    direction?: 'asc' | 'desc'
  ) => {
    setPage(1);
    if (!direction) {
      setCompanyReviewsSort({ id: 'desc' });
      return;
    }
    setCompanyReviewsSort({ [field]: direction });
  };

  const handleCandidateReviewsSort = (
    field: string,
    direction?: 'asc' | 'desc'
  ) => {
    setPage(1);
    if (!direction) {
      setCandidateReviewsSort({ id: 'desc' });
      return;
    }
    setCandidateReviewsSort({ [field]: direction });
  };

  return (
    <PageContainer title='Отзывы'>
      <Tabs value={activeTab} onChange={handleTabChange} variant='pills'>
        <Tabs.List>
          <Tabs.Tab value={TAB_COMPANY_REVIEWS} h={42}>
            Отзывы компаний
          </Tabs.Tab>
          <Tabs.Tab value={TAB_CANDIDATE_REVIEWS} h={42}>
            Отзывы кандидатов
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={TAB_COMPANY_REVIEWS} pt={32}>
          <Flex direction='column' gap={32}>
            <ReviewsFilters
              setFilter={setCompanyReviewsFilter}
              preserveParams={{ tab: TAB_COMPANY_REVIEWS }}
            />
            <ReviewsTable
              reviews={companyReviewsQuery.data?.data ?? []}
              totalRecords={companyReviewsQuery.data?.pagination.total}
              page={page}
              setPage={setPage}
              onRecordsPerPageChange={setPageSize}
              recordsPerPageOptions={INDEX_PAGE_SIZES}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isFilledFilters={Object.keys(companyReviewsFilter).length > 0}
              onSortChange={handleCompanyReviewsSort}
              currentSort={companyReviewsSort}
              isLoading={
                companyReviewsQuery.isLoading || companyReviewsQuery.isFetching
              }
              columnOrder='company-first'
              emptySubDesc='Компании ещё не оставили отзывы'
              onRowClick={review =>
                openCandidateReviewDetailModal(review as CandidateReview)
              }
            />
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value={TAB_CANDIDATE_REVIEWS} pt={32}>
          <Flex direction='column' gap={32}>
            <ReviewsFilters
              setFilter={setCandidateReviewsFilter}
              preserveParams={{ tab: TAB_CANDIDATE_REVIEWS }}
            />
            <ReviewsTable
              reviews={candidateReviewsQuery.data?.data ?? []}
              totalRecords={candidateReviewsQuery.data?.pagination.total}
              page={page}
              setPage={setPage}
              onRecordsPerPageChange={setPageSize}
              recordsPerPageOptions={INDEX_PAGE_SIZES}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isFilledFilters={Object.keys(candidateReviewsFilter).length > 0}
              onSortChange={handleCandidateReviewsSort}
              currentSort={candidateReviewsSort}
              isLoading={
                candidateReviewsQuery.isLoading ||
                candidateReviewsQuery.isFetching
              }
              columnOrder='candidate-first'
              emptySubDesc='Кандидаты ещё не оценили компанию'
              onRowClick={review =>
                openCompanyReviewDetailModal(review as CompanyReview)
              }
            />
          </Flex>
        </Tabs.Panel>
      </Tabs>
    </PageContainer>
  );
};
